import {
  OPTION_FACTOR_DEFINITIONS,
  OPTION_FACTOR_KEYS,
} from "./NinaOptionsScannerSchema";
import { roundOptionNumber } from "./NinaOptionsScannerValidation";

const evidenceQuality = {
  simulated: 0.8,
  "system-derived": 0.9,
  "static-demo": 0.7,
  hypothesis: 0.45,
  unavailable: 0,
};

export function calculateOptionsScores(snapshot, now) {
  // Suitability is centered at 50. Each factor contributes a signed adjustment
  // from -weight to +weight using (score - 50) / 50. Conflicting evidence is
  // always adverse, and unavailable evidence receives the full -weight
  // contribution. The final 50 + signedTotal / 2 mapping remains within 0–100
  // and mathematically prevents evidence removal from strengthening a setup.
  let signedSuitability = 0;
  let confidenceCredit = 0;
  let availableWeight = 0;
  let contradictionWeight = 0;
  const factors = OPTION_FACTOR_KEYS.map((key) => {
    const definition = OPTION_FACTOR_DEFINITIONS[key];
    const signal = snapshot.signals[key];
    if (signal.status === "unavailable") {
      const contribution = -definition.weight;
      signedSuitability += contribution;
      return {
        key,
        ...definition,
        ...signal,
        contribution,
        confidenceCredit: 0,
      };
    }
    const normalizedDirection = (signal.score - 50) / 50;
    const contribution = roundOptionNumber(
      signal.status === "conflicting"
        ? -Math.abs(normalizedDirection) * definition.weight
        : normalizedDirection * definition.weight
    );
    const agreement =
      signal.status === "conflicting" ? 0.25 : signal.status === "negative" ? 0.55 : 1;
    const factorConfidence = roundOptionNumber(
      definition.weight * evidenceQuality[signal.evidenceStatus] * agreement
    );
    signedSuitability += contribution;
    confidenceCredit += factorConfidence;
    availableWeight += definition.weight;
    if (signal.status === "conflicting") contradictionWeight += definition.weight;
    return {
      key,
      ...definition,
      ...signal,
      contribution,
      confidenceCredit: factorConfidence,
    };
  });
  const ageSeconds = Math.max(0, (now - Date.parse(snapshot.updatedAt)) / 1000);
  const freshnessCredit =
    ageSeconds <= 120 ? 5 : ageSeconds <= 900 ? 3 : ageSeconds <= 3600 ? 1 : 0;
  const stabilityCredit = snapshot.stability * 0.05;
  const completeness = roundOptionNumber(availableWeight);
  const missingCritical = factors
    .filter((factor) => factor.critical && factor.status === "unavailable")
    .map((factor) => factor.label);
  const confidenceRaw =
    confidenceCredit * 0.9 + freshnessCredit + stabilityCredit;
  const confidenceCap = missingCritical.length ? 69 : 100;
  const confidence = Math.min(confidenceCap, Math.round(confidenceRaw));
  const underlyingFactors = factors.filter((factor) =>
    ["underlyingAlignment", "momentum"].includes(factor.key)
  );
  const underlyingSmartFlow = Math.round(
    underlyingFactors.reduce((sum, factor) => {
      if (factor.status === "unavailable") return sum;
      if (factor.status === "conflicting") {
        return sum + Math.min(factor.score, 40);
      }
      return sum + factor.score;
    }, 0) /
      underlyingFactors.length
  );
  const suitability = 50 + signedSuitability / 2;
  return {
    suitability: Math.max(0, Math.min(100, Math.round(suitability))),
    smartFlow: Math.max(0, Math.min(100, underlyingSmartFlow)),
    confidence: Math.max(0, Math.min(100, confidence)),
    completeness,
    freshnessCredit,
    stabilityCredit: roundOptionNumber(stabilityCredit),
    contradictionWeight,
    missingCritical,
    factors,
  };
}
